/**
 * ShareModal - Reusable share modal functionality
 * Used by both dashboard.js and viewer.js
 */
class ShareModal {
  constructor() {
    this.currentShareItem = null;
    this.initialized = false;
  }

  /**
   * Initialize the share modal event listeners
   * Should be called once when the page loads
   */
  init() {
    if (this.initialized) return;

    const shareModal = document.getElementById('shareModal');
    if (!shareModal) return;

    // Close button
    const closeBtn = shareModal.querySelector('.close-modal');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    // Click outside to close
    shareModal.addEventListener('mousedown', (e) => {
      shareModal._clickedBackdrop = (e.target === shareModal);
    });
    shareModal.addEventListener('mouseup', (e) => {
      if (e.target === shareModal && shareModal._clickedBackdrop) {
        this.close();
      }
      shareModal._clickedBackdrop = false;
    });

    // Send invite button
    const btnSendInvite = document.getElementById('btnSendInvite');
    if (btnSendInvite) {
      btnSendInvite.addEventListener('click', () => this.sendInvite());
    }

    // Enter key in email input
    const emailInput = document.getElementById('shareEmail');
    if (emailInput) {
      emailInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.sendInvite();
        }
      });
    }

    // Public link toggle
    const publicLinkToggle = document.getElementById('publicLinkToggle');
    if (publicLinkToggle) {
      publicLinkToggle.addEventListener('change', async (e) => {
        const enabled = e.target.checked;
        const casePath = this.currentShareItem?.file_id || this.currentShareItem?.id;
        if (!casePath) return;

        const roleSelect = document.getElementById('shareRole');
        const role = roleSelect?.value || 'viewer';

        const expirySelect = document.getElementById('shareLinkExpiry');
        const expiryHours = expirySelect?.value === 'never' ? null : parseInt(expirySelect?.value || '24');

        try {
          const response = await window.Auth.authenticatedFetch(
            `/api/cases/${encodeURIComponent(casePath)}/public-link`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ enabled, role, expiry_hours: expiryHours })
            }
          );

          if (response.ok) {
            const result = await response.json();
            this.updatePublicLinkUI(enabled, result.expires_at);
            if (enabled) {
              const expiryMsg = result.expires_at ? ` Link expires in ${expiryHours} hours.` : '';
              this.notify('Public link enabled. Anyone with the link can now view this case.' + expiryMsg, 'success');
              // Track share link creation
              if (window.Analytics) {
                window.Analytics.trackShareLink('public', expiryHours);
              }
            } else {
              this.notify('Public link disabled. The link will no longer work.', 'success');
            }
          } else {
            // Revert toggle on error
            publicLinkToggle.checked = !enabled;
            const error = await response.json().catch(() => ({}));
            this.notify(error.detail || 'Failed to update public link', 'error');
          }
        } catch (err) {
          console.error('Public link toggle error:', err);
          publicLinkToggle.checked = !enabled;
          this.notify('Failed to update public link', 'error');
        }
      });
    }

    // Expiry dropdown change listener
    const expirySelect = document.getElementById('shareLinkExpiry');
    if (expirySelect) {
      expirySelect.addEventListener('change', async (e) => {
        const publicLinkToggle = document.getElementById('publicLinkToggle');
        // Only update if link is currently enabled
        if (!publicLinkToggle || !publicLinkToggle.checked) return;

        const casePath = this.currentShareItem?.file_id || this.currentShareItem?.id;
        if (!casePath) return;

        const roleSelect = document.getElementById('shareRole');
        const role = roleSelect?.value || 'viewer';
        const expiryHours = e.target.value === 'never' ? null : parseInt(e.target.value || '24');

        try {
          const response = await window.Auth.authenticatedFetch(
            `/api/cases/${encodeURIComponent(casePath)}/public-link`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ enabled: true, role, expiry_hours: expiryHours })
            }
          );

          if (response.ok) {
            const result = await response.json();
            this.updatePublicLinkUI(true, result.expires_at);
            const expiryMsg = result.expires_at ? `Link will expire in ${expiryHours} hours` : 'Link will never expire';
            this.notify(expiryMsg, 'success');
          } else {
            const error = await response.json().catch(() => ({}));
            this.notify(error.detail || 'Failed to update expiry', 'error');
          }
        } catch (err) {
          console.error('Expiry update error:', err);
          this.notify('Failed to update expiry', 'error');
        }
      });
    }

    // Copy link button
    const btnCopyLink = document.getElementById('btnCopyLink');
    if (btnCopyLink) {
      btnCopyLink.addEventListener('click', async () => {
        const linkInput = document.getElementById('publicLinkInput');
        const publicLinkToggle = document.getElementById('publicLinkToggle');

        if (linkInput && linkInput.value && this.currentShareItem) {
          try {
            // If public link is not enabled, enable it first
            if (publicLinkToggle && !publicLinkToggle.checked) {
              const casePath = this.currentShareItem.file_id || this.currentShareItem.id;
              const roleSelect = document.getElementById('shareRole');
              const role = roleSelect?.value || 'viewer';

              const expirySelect = document.getElementById('shareLinkExpiry');
              const expiryHours = expirySelect?.value === 'never' ? null : parseInt(expirySelect?.value || '24');

              const enableResponse = await window.Auth.authenticatedFetch(
                `/api/cases/${encodeURIComponent(casePath)}/public-link`,
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ enabled: true, role: role, expiry_hours: expiryHours })
                }
              );

              if (enableResponse.ok) {
                const result = await enableResponse.json();
                publicLinkToggle.checked = true;
                this.updatePublicLinkUI(true, result.expires_at);
              }
            }

            // Use modern clipboard API
            await navigator.clipboard.writeText(linkInput.value);
            // Show copied feedback
            btnCopyLink.classList.add('copied');
            const originalHTML = btnCopyLink.innerHTML;
            btnCopyLink.innerHTML = '<img src="static/assets/icons/check.svg" alt="" width="16" height="16" style="opacity: 0.9;"><span>Copied!</span>';
            setTimeout(() => {
              btnCopyLink.classList.remove('copied');
              btnCopyLink.innerHTML = originalHTML;
            }, 2000);
            this.notify('Link copied to clipboard!', 'success');
          } catch (err) {
            console.error('Copy link error:', err);
            // Fallback for older browsers
            linkInput.select();
            document.execCommand('copy');
            this.notify('Link copied to clipboard!', 'success');
          }
        }
      });
    }

    // Regenerate link button
    const btnRegenerateLink = document.getElementById('btnRegenerateLink');
    if (btnRegenerateLink) {
      btnRegenerateLink.addEventListener('click', async () => {
        const publicLinkToggle = document.getElementById('publicLinkToggle');
        // Only allow regeneration if link is currently enabled
        if (!publicLinkToggle || !publicLinkToggle.checked) {
          this.notify('Enable the public link first before regenerating', 'warning');
          return;
        }

        if (!confirm('Regenerate share link?\n\nThis will create a new link and invalidate the old one. Anyone with the old link will lose access.\n\nAre you sure?')) {
          return;
        }

        const casePath = this.currentShareItem?.file_id || this.currentShareItem?.id;
        if (!casePath) return;

        try {
          const response = await window.Auth.authenticatedFetch(
            `/api/cases/${encodeURIComponent(casePath)}/regenerate-link`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' }
            }
          );

          if (response.ok) {
            const result = await response.json();
            // Update the link input with the new token-based URL
            const linkInput = document.getElementById('publicLinkInput');
            if (linkInput && result.new_token) {
              const fullUrl = `${window.location.origin}/index.html?token=${result.new_token}`;
              linkInput.value = fullUrl;
            }
            this.notify('Link regenerated successfully! Old link is now invalid.', 'success');
          } else {
            const error = await response.json().catch(() => ({}));
            this.notify(error.detail || 'Failed to regenerate link', 'error');
          }
        } catch (err) {
          console.error('Regenerate link error:', err);
          this.notify('Failed to regenerate link', 'error');
        }
      });
    }

    this.initialized = true;
  }

  /**
   * Build a case reference string using file_id (UUID) when available
   */
  buildCaseRef(item) {
    if (item.file_id) {
      const sub = item.file_id_sub_path || '';
      if (sub) {
        const cleanSub = sub.startsWith('/') ? sub.substring(1) : sub;
        return `${item.file_id}/${cleanSub}`;
      }
      return item.file_id;
    }
    return item.path || item.id;
  }

  /**
   * Open the share modal
   * @param {string} id - The item ID or path
   * @param {string} type - 'case' or 'session'
   * @param {string} name - Display name for the modal title
   * @param {string} file_id - Optional UUID file ID
   * @param {string} file_id_sub_path - Optional sub-path within the file
   */
  open(id, type, name, file_id = null, file_id_sub_path = '') {
    this.init(); // Ensure initialized

    this.currentShareItem = { id, type, name, file_id, file_id_sub_path };
    const modal = document.getElementById('shareModal');

    // If modal not loaded yet, wait and retry
    if (!modal) {
      console.warn('[ShareModal] Modal not loaded yet, retrying in 100ms...');
      setTimeout(() => this.open(id, type, name, file_id, file_id_sub_path), 100);
      return;
    }

    const modalTitle = document.getElementById('shareModalTitle');

    if (modalTitle) {
      modalTitle.textContent = `Share "${name || 'Simulation'}"`;
    }

    // Clear email input and reset role selector
    const emailInput = document.getElementById('shareEmail');
    if (emailInput) emailInput.value = '';
    const roleSelect = document.getElementById('shareRole');
    if (roleSelect) roleSelect.value = 'viewer';

    // Link will be populated by loadPublicLinkStatus (token-based URL)
    const linkInput = document.getElementById('publicLinkInput');
    if (linkInput) {
      linkInput.value = 'Loading...';
    }

    // Reset public link toggle
    const publicLinkToggle = document.getElementById('publicLinkToggle');
    if (publicLinkToggle) {
      publicLinkToggle.checked = false;
    }
    this.updatePublicLinkUI(false);

    // Open modal with RAF to prevent click-through
    requestAnimationFrame(() => {
      if (modal) {
        modal.classList.add('active');
      }
      const emailInputEl = document.getElementById('shareEmail');
      if (emailInputEl) {
        emailInputEl.focus();
      }
    });

    // Load existing shares
    const caseRef = file_id || id;
    this.loadShares(caseRef, type);

    // Load public link status
    this.loadPublicLinkStatus(caseRef);
  }

  /**
   * Close the share modal
   */
  close() {
    const modal = document.getElementById('shareModal');
    if (modal) {
      modal.classList.remove('active');
    }
    this.currentShareItem = null;
  }

  /**
   * Update the public link UI based on enabled state
   * @param {boolean} enabled - Whether public link is enabled
   * @param {string} expiresAt - ISO datetime string when link expires (optional)
   */
  updatePublicLinkUI(enabled, expiresAt = null) {
    const statusText = document.getElementById('publicLinkStatusText');
    const infoText = document.getElementById('publicLinkInfo');
    const linkContainer = document.getElementById('publicLinkContainer');
    const expiryInfo = document.getElementById('shareLinkExpiryInfo');

    if (statusText) {
      statusText.textContent = enabled ? 'Enabled' : 'Not enabled';
      statusText.style.color = enabled ? '#10b981' : '#94a3b8';
    }

    if (infoText) {
      infoText.textContent = enabled
        ? 'Anyone with the link can view this simulation.'
        : 'Enable to allow anyone with the link to view this simulation.';
    }

    if (linkContainer) {
      linkContainer.style.opacity = enabled ? '1' : '0.5';
    }

    // Show expiry info if link is enabled and has an expiry time
    if (expiryInfo && enabled && expiresAt) {
      const expiryDate = new Date(expiresAt);
      const now = new Date();
      const hoursUntilExpiry = Math.max(0, Math.round((expiryDate - now) / (1000 * 60 * 60)));
      expiryInfo.textContent = `Expires in ${hoursUntilExpiry}h`;
      expiryInfo.style.display = 'inline';
    } else if (expiryInfo) {
      expiryInfo.style.display = 'none';
    }
  }

  /**
   * Load the current public link status for a case
   */
  async loadPublicLinkStatus(casePath) {
    try {
      const response = await window.Auth.authenticatedFetch(
        `/api/cases/${encodeURIComponent(casePath)}/public-link`
      );

      if (response.ok) {
        const data = await response.json();
        const publicLinkToggle = document.getElementById('publicLinkToggle');

        if (publicLinkToggle) {
          publicLinkToggle.checked = data.enabled || false;
        }
        this.updatePublicLinkUI(data.enabled || false, data.share_expires_at);

        // Update link URL to use token if available (more secure than case path)
        if (data.public_access_token) {
          const linkInput = document.getElementById('publicLinkInput');
          if (linkInput) {
            linkInput.value = `${window.location.origin}/index.html?token=${data.public_access_token}`;
          }
        }

        // Also update role selector if we have a saved role
        if (data.role) {
          const roleSelect = document.getElementById('shareRole');
          if (roleSelect) {
            roleSelect.value = data.role;
          }
        }

        // Update expiry selector based on saved expiry time
        if (data.share_enabled_at && data.share_expires_at) {
          const enabledAt = new Date(data.share_enabled_at);
          const expiresAt = new Date(data.share_expires_at);
          const expiryHours = Math.round((expiresAt - enabledAt) / (1000 * 60 * 60));

          const expirySelect = document.getElementById('shareLinkExpiry');
          if (expirySelect) {
            // Try to match the saved expiry to one of our options
            const options = ['1', '6', '24', '168', '720'];
            const closestOption = options.find(opt => Math.abs(parseInt(opt) - expiryHours) < 1) || '24';
            expirySelect.value = closestOption;
          }
        } else if (data.share_enabled_at && !data.share_expires_at) {
          // No expiry set - select "never"
          const expirySelect = document.getElementById('shareLinkExpiry');
          if (expirySelect) {
            expirySelect.value = 'never';
          }
        }
      }
    } catch (err) {
      console.error('Failed to load public link status:', err);
    }
  }

  /**
   * Load existing shares/collaborators for an item
   */
  async loadShares(id, type) {
    const collaboratorsList = document.getElementById('collaboratorsList');
    if (!collaboratorsList) return;

    try {
      let endpoint;
      if (type === 'session') {
        endpoint = `/api/sessions/${id}/collaborators`;
      } else {
        endpoint = `/api/cases/${encodeURIComponent(id)}/collaborators`;
      }

      const response = await window.Auth.authenticatedFetch(endpoint);

      if (response.ok) {
        const result = await response.json();
        const collaborators = result.collaborators || [];

        if (collaborators.length === 0) {
          collaboratorsList.innerHTML = '<div class="empty-collaborators">No collaborators yet</div>';
        } else {
          collaboratorsList.innerHTML = collaborators.map(c => `
            <div class="collaborator-item" data-user-id="${c.id}">
              <div class="collaborator-info">
                <span class="collaborator-name">${this.escapeHtml(c.name || c.email)}</span>
                <span class="collaborator-email">${this.escapeHtml(c.email)}</span>
              </div>
              <span class="collaborator-role">${c.role}</span>
              <button class="remove-collaborator-btn" title="Remove collaborator">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          `).join('');

          // Attach click handlers to remove buttons
          collaboratorsList.querySelectorAll('.remove-collaborator-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
              e.stopPropagation();
              const item = btn.closest('.collaborator-item');
              const userId = item.dataset.userId;
              await this.removeCollaborator(id, type, userId);
            });
          });
        }
      } else {
        collaboratorsList.innerHTML = '<div class="empty-collaborators">No collaborators yet</div>';
      }
    } catch (e) {
      console.error('Failed to load shares:', e);
      collaboratorsList.innerHTML = '<div class="empty-collaborators">No collaborators yet</div>';
    }
  }

  /**
   * Send share invitation
   */
  async sendInvite() {
    const email = document.getElementById('shareEmail')?.value.trim();
    const roleSelect = document.getElementById('shareRole');
    const role = roleSelect ? roleSelect.value : 'viewer';

    if (!email) {
      this.notify('Please enter an email address', 'error');
      return;
    }

    if (!this.currentShareItem) {
      this.notify('No item selected for sharing', 'error');
      return;
    }

    try {
      let endpoint;
      if (this.currentShareItem.type === 'session') {
        endpoint = `/api/sessions/${encodeURIComponent(this.currentShareItem.id)}/share`;
      } else {
        const caseRef = this.currentShareItem.file_id || this.currentShareItem.id;
        endpoint = `/api/cases/${encodeURIComponent(caseRef)}/share`;
      }

      const response = await window.Auth.authenticatedFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, role })
      });

      const result = await response.json();

      if (response.ok && result.status === 'success') {
        this.notify(result.message || `Shared with ${email}`, 'success');
        document.getElementById('shareEmail').value = '';
        this.loadShares(this.currentShareItem.file_id || this.currentShareItem.id, this.currentShareItem.type);
      } else {
        throw new Error(result.detail || result.message || 'Failed to share');
      }
    } catch (e) {
      this.notify('Failed to send invitation: ' + e.message, 'error');
    }
  }

  /**
   * Remove a collaborator from a shared simulation
   */
  async removeCollaborator(itemId, itemType, userId) {
    if (!confirm('Are you sure you want to remove this collaborator?')) {
      return;
    }

    try {
      let endpoint;
      if (itemType === 'session') {
        endpoint = `/api/sessions/${itemId}/collaborators/${userId}`;
      } else {
        endpoint = `/api/cases/${encodeURIComponent(itemId)}/collaborators/${userId}`;
      }

      const response = await window.Auth.authenticatedFetch(endpoint, {
        method: 'DELETE'
      });

      if (response.ok) {
        this.notify('Collaborator removed successfully', 'success');
        // Refresh the collaborators list
        this.loadShares(itemId, itemType);
      } else {
        const error = await response.json();
        this.notify(error.detail || 'Failed to remove collaborator', 'error');
      }
    } catch (e) {
      console.error('Failed to remove collaborator:', e);
      this.notify('Failed to remove collaborator', 'error');
    }
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
  }

  /**
   * Show notification
   */
  notify(message, type = 'info') {
    if (typeof Utils !== 'undefined' && Utils.notify) {
      Utils.notify(message, type);
    } else {
      if (type === 'error') {
        alert(message);
      } else {
        console.log(`[${type}] ${message}`);
      }
    }
  }
}

// Create global instance
window.shareModal = new ShareModal();
